import React, { useCallback, useState } from "react";
import { Button, Card, Input, Tag, message } from "antd";
import type {
  ActionPlanAiAmbiguousMatch,
  ActionPlanAiTaskListState,
  ActionPlanAiTaskPickAction,
  ActionPlanAiViewParams,
} from "@/types/action-plan-v2/ActionPlanAiTaskList";
import {
  bucketLabel,
  fetchActionPlanAiTaskListPage,
  viewParamsAfterPick,
} from "@/components/common/ActionPlanV2/actionPlanAiTaskList";
import { ACTION_PLAN_AI_STATUSES } from "@/components/common/ActionPlanV2/actionPlanAiStatus";
import cardStyles from "./ActionPlanTaskListCard.module.css";
import apCard from "./apCard.module.css";

type Props = {
  state: ActionPlanAiTaskListState;
  onListChange: (next: ActionPlanAiTaskListState) => void;
  onPick: (action: ActionPlanAiTaskPickAction, taskId: string) => void | Promise<void>;
};

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "Tất cả" },
  ...ACTION_PLAN_AI_STATUSES,
];

const BUCKET_COLOR: Record<string, string> = {
  overdue: "red",
  today: "orange",
  upcoming: "blue",
  in_progress: "green",
};

function formatDate(value?: string): string {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : value;
}

function overdueDays(value?: string): number {
  if (!value) return 0;
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((today.getTime() - due.getTime()) / 86400000));
}

export const ActionPlanTaskListCard: React.FC<Props> = ({ state, onListChange, onPick }) => {
  const { list } = state;
  const [qDraft, setQDraft] = useState(list.query || "");
  const intent = list.intent || "browse";
  const view = list.view;
  const viewing = Boolean(view && view.scope && view.scope !== "self");
  const showUpdate = intent === "update" || intent === "browse";
  const showChild = intent === "create_child" || intent === "browse";
  const showDelete = intent === "delete" || intent === "browse";
  const ambiguous = list.ambiguous ?? [];
  const groups = list.groups ?? [];
  const activeStatus = view?.params.trangThai ?? "";

  const loadPage = useCallback(
    async (page: number, query?: string, viewParams?: ActionPlanAiViewParams) => {
      onListChange({ ...state, loading: true });
      try {
        const nextList = await fetchActionPlanAiTaskListPage(list, page, query, viewParams);
        onListChange({ list: nextList, loading: false });
      } catch (e) {
        message.error(e instanceof Error ? e.message : "Không tải được trang.");
        onListChange({ ...state, loading: false });
      }
    },
    [list, onListChange, state],
  );

  const pickAmbiguous = (match: ActionPlanAiAmbiguousMatch) => {
    if (!view) return;
    void loadPage(1, undefined, viewParamsAfterPick(view.params, match));
  };

  const changeStatus = (trangThai: string) => {
    if (!view) return;
    void loadPage(1, undefined, { ...view.params, trangThai: trangThai || undefined });
  };

  const filterByPerson = (id: string) => {
    if (!view) return;
    const next = { ...view.params, nguoiPhuTrachId: id };
    delete next.tenNhanVien;
    void loadPage(1, undefined, next);
  };

  const subject =
    view?.tenBoPhan
      ? `Phòng ${view.tenBoPhan}`
      : view?.tenNhanVien
        ? `Việc của ${view.tenNhanVien}`
        : view?.tenNguoiTao
          ? `Việc do ${view.tenNguoiTao} giao`
          : "";

  const title = ambiguous.length
    ? "Chọn đúng đối tượng"
    : subject ||
      (intent === "update"
        ? "Chọn việc cần sửa"
        : intent === "create_child"
          ? "Chọn việc cha (việc con)"
          : intent === "delete"
            ? "Chọn việc cần xoá"
            : "Danh sách công việc của bạn");

  return (
    <Card
      size="small"
      title={title}
      className={`${cardStyles.card} ${apCard.card}`}
      extra={
        viewing ? (
          <Tag bordered={false} color="default" className="!m-0 !text-[11px]">
            Chỉ xem
          </Tag>
        ) : null
      }
    >
      {ambiguous.length > 0 ? (
        <div className={cardStyles.chooser}>
          <div className={cardStyles.meta}>Có nhiều kết quả trùng tên, chọn một:</div>
          {ambiguous.map((m) => (
            <Button
              key={m.id}
              size="small"
              block
              disabled={state.loading}
              className={cardStyles.chooserBtn}
              onClick={() => pickAmbiguous(m)}
            >
              <span className={cardStyles.title}>{m.ten}</span>
              {m.boPhan ? <span className={cardStyles.meta}>{m.boPhan}</span> : null}
            </Button>
          ))}
        </div>
      ) : (
        <>
          {viewing ? (
            <div className={cardStyles.filters}>
              {STATUS_FILTERS.map((f) => (
                <Button
                  key={f.value || "all"}
                  size="small"
                  type={activeStatus === f.value ? "primary" : "default"}
                  disabled={state.loading}
                  onClick={() => changeStatus(f.value)}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          ) : null}

          {viewing && groups.length > 1 ? (
            <div className={cardStyles.groups}>
              {groups.slice(0, 6).map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className={cardStyles.groupChip}
                  disabled={state.loading}
                  onClick={() => filterByPerson(g.id)}
                  title="Chỉ xem việc của người này"
                >
                  {g.ten || g.id} · {g.soViec} việc
                  {g.treHan > 0 ? <span className={cardStyles.groupLate}> · {g.treHan} trễ</span> : null}
                </button>
              ))}
            </div>
          ) : null}

          <Input.Search
            size="small"
            allowClear
            placeholder="Lọc theo tên việc…"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            onSearch={(val) => void loadPage(1, val.trim())}
            loading={state.loading}
            className="!mb-2"
          />

          {list.items.length === 0 ? (
            <div className={cardStyles.meta}>Không có việc phù hợp.</div>
          ) : (
            list.items.map((item) => {
              const late = item.bucket === "overdue" ? overdueDays(item.ngayKetThuc) : 0;
              const mine = (item.canEdit ?? item.isMine) !== false;
              return (
                <div key={item.id} className={cardStyles.row}>
                  <div className={cardStyles.rowTop}>
                    <span className={cardStyles.title}>{item.tenCongViec || item.id}</span>
                    <span className={cardStyles.tags}>
                      {item.isDuAn ? (
                        <Tag bordered={false} className="!m-0 !text-[11px]">
                          Dự án
                        </Tag>
                      ) : null}
                      {item.bucket && bucketLabel(item.bucket) ? (
                        <Tag
                          bordered={false}
                          color={BUCKET_COLOR[item.bucket]}
                          className="!m-0 !text-[11px]"
                        >
                          {bucketLabel(item.bucket)}
                        </Tag>
                      ) : null}
                    </span>
                  </div>
                  {item.ngayKetThuc ? (
                    <div className={cardStyles.meta}>
                      Hạn: {formatDate(item.ngayKetThuc)}
                      {late > 0 ? <span className={cardStyles.late}> · trễ {late} ngày</span> : null}
                    </div>
                  ) : null}
                  {viewing && (item.tenNguoiPhuTrach || item.tenNguoiTao) ? (
                    <div className={cardStyles.meta}>
                      {item.tenNguoiPhuTrach ? `Phụ trách: ${item.tenNguoiPhuTrach}` : ""}
                      {item.tenNguoiPhuTrach && item.tenNguoiTao ? " · " : ""}
                      {item.tenNguoiTao ? `Giao bởi: ${item.tenNguoiTao}` : ""}
                    </div>
                  ) : null}
                  {mine ? (
                    <div className={cardStyles.actions}>
                      {showUpdate ? (
                        <Button
                          size="small"
                          type="primary"
                          disabled={state.loading}
                          onClick={() => void onPick("update", item.id)}
                        >
                          Sửa
                        </Button>
                      ) : null}
                      {showChild ? (
                        <Button
                          size="small"
                          disabled={state.loading}
                          onClick={() => void onPick("create_child", item.id)}
                        >
                          Việc con
                        </Button>
                      ) : null}
                      {showDelete ? (
                        <Button
                          size="small"
                          danger
                          disabled={state.loading}
                          onClick={() => void onPick("delete", item.id)}
                        >
                          Xoá
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <div className={cardStyles.meta}>Chỉ xem — việc của người khác.</div>
                  )}
                </div>
              );
            })
          )}

          <div className={cardStyles.pager}>
            <span className={cardStyles.pagerInfo}>
              Trang {list.page}/{Math.max(list.totalPages, 1)} · {list.total} việc
            </span>
            <div className="flex gap-1">
              <Button
                size="small"
                disabled={state.loading || list.page <= 1}
                onClick={() => void loadPage(list.page - 1)}
              >
                Trước
              </Button>
              <Button
                size="small"
                disabled={state.loading || list.page >= list.totalPages}
                onClick={() => void loadPage(list.page + 1)}
              >
                Sau
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};
